<template>
  <div :class="[$style.panel, { [$style.mini]: isMini }]">
    <TenantOrgaSelect v-if="!isMini && !isDesktop" :class="$style.tenantOrgaSelect" />
    <div :class="$style.sidebarNavTabs">
      <SidebarNavHeader
        v-model="searchTerm"
        :is-mini="isMini"
        :show-settings="true"
        :nodes="allNodes"
        :set-all-expanded="setAllExpanded"
        :all-expanded="isAllExpanded"
        :all-collapsed="isAllCollapsed"
      />
    </div>

    <div :class="$style.innerPanel">
      <div :class="$style.mainMenuSection">
        <div v-show="!isMini">
          <SidebarNavDefault :nodes="currentNodes" :search-term="searchTerm" />
        </div>
        <div v-show="isMini">
          <SidebarNavMini :nodes="currentNodes" />
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="!isDesktop"
    :class="$style.sidebarNavBackground"
    @touchmove.prevent.self
    @click="layoutStore.toggleBarVisibility('navigation')"
  />
</template>

<script setup lang="ts">
import type { Module, ModuleFolderStructureItem, TemplateInfo } from "@scayla/api-client-fetch";
import { BuiltinModuleTag, TenantType } from "@scayla/api-client-fetch";
import { useScaBreakpoints } from "@scayla/core";
import { useQueries } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteUpdate } from "vue-router";

import { provideRefreshTrigger } from "@/composables/refreshTrigger";
import { getModuleFoldersStructureQuery } from "@/composables/useModuleFoldersStructure";
import maybeTranslateCommonTerm from "@/i18n/helpers";
import { Routes } from "@/router";
import { useLayoutStore } from "@/store/layoutStore";
import { useWorkspace } from "@/store/workspace";
import { getModuleIcon } from "@/utils/moduleUtils";
import type { NavNode } from "@/utils/navUtils";

import { useAccountingNav } from "./accounting";
import { useHomeNav } from "./home";
import { useSettingsNav } from "./settings";
import SidebarNavDefault from "./sidebarNav/SidebarNavDefault.vue";
import SidebarNavHeader from "./sidebarNav/SidebarNavHeader.vue";
import SidebarNavMini from "./sidebarNav/SidebarNavMini.vue";
import { useTenantManagementNav } from "./tenantManagement";
import TenantOrgaSelect from "./TenantOrgaSelect.vue";
import { useUserSettingsNav } from "./userSettings";

type FavoriteItem =
  | { type: "template"; data: TemplateInfo }
  | { type: "folder"; data: ModuleFolderStructureItem }
  | { type: "node"; data: NavNode };

const searchTerm = ref("");
const { t: $t } = useI18n();
const layoutStore = useLayoutStore();
const workspace = useWorkspace();

const homeNav = useHomeNav();
const accountingNav = useAccountingNav();
const tenantManagementNav = useTenantManagementNav();
const settingsNav = useSettingsNav();
const userSettingsNav = useUserSettingsNav();

const { isDesktop } = useScaBreakpoints();
const prevNavWasVisible = ref<boolean | null>(null);

const isMini = computed(() => layoutStore.sidebarNavMode === "mini");

const getModuleKey = (id: string) => `_module_${id}`;

const prefilteredModules = computed(() => {
  return (
    workspace.modules?.filter((module) => {
      if (module.settingsModule) return false;
      return workspace.getModulePermissions(module.id).active === true;
    }) ?? []
  );
});

const filteredModules = computed(() => {
  return prefilteredModules.value;
});

const emailModule = computed(() => workspace.modules?.find((m) => m.builtinModule === BuiltinModuleTag.EMAILS));

const docsModule = computed(() => workspace.modules?.find((m) => m.builtinModule === BuiltinModuleTag.DOCUMENTS));

const folderQueries = computed(() => {
  if (!prefilteredModules.value) return [];
  return prefilteredModules.value.map((module) => ({
    ...getModuleFoldersStructureQuery(module.id),
  }));
});

function getModulePinnedItems(moduleId: string): FavoriteItem[] {
  if (moduleId !== docsModule.value?.id) return [];
  const emailId = emailModule.value?.id;
  if (!emailId) return [];

  const emailObj = foldersStructure.value.find((o) => o.moduleId === emailId);
  const emailChildren: NavNode[] = [];

  if (emailObj) {
    let emailTemplates = emailObj.data.templates ?? [];
    emailTemplates = emailTemplates.filter((t) => workspace.getTemplatePermissions(t.id).read && t.showInNavigation);
    for (const t of emailTemplates) {
      emailChildren.push({
        label: t.name,
        layoutStoreKey: t.id,
        route: {
          name: Routes.MODULE,
          params: { moduleId: emailId },
          query: { templateId: t.id, tenant: workspace.tenantId },
        },
        indexId: t.indexId,
      });
    }
    for (const f of emailObj.data.folders ?? []) {
      emailChildren.push(mapFolderToNavNode(f, emailId));
    }
    emailChildren.sort((a, b) => (a.indexId ?? Infinity) - (b.indexId ?? Infinity));
  }

  return [
    {
      type: "node",
      data: {
        label: $t("common.label.emails"),
        layoutStoreKey: getModuleKey(emailId),
        route: { name: Routes.MODULE, params: { moduleId: emailId }, query: { tenant: workspace.tenantId } },
        children: emailChildren,
        indexId: -1,
      },
    },
  ];
}

function filterModuleForFavorites(
  module: Module,
  layoutStore: ReturnType<typeof useLayoutStore>,
  getModuleKey: (id: string) => string,
  getModuleTopLevelItems: (id: string) => FavoriteItem[],
): (Module & { filteredItems: FavoriteItem[]; isFavorite: boolean }) | null {
  const isFavorite = layoutStore.isFavorite(getModuleKey(module.id));
  const items = getModuleTopLevelItems(module.id);

  let filteredItems: FavoriteItem[] = [];

  if (isFavorite) {
    // Show all children if module is favorite
    filteredItems = items;
  } else if (items && items.length > 0) {
    filteredItems = items
      .map((item): FavoriteItem | null => {
        if (item.type === "template") {
          const isTemplateFavorite = layoutStore.isFavorite(item.data.id ?? "");
          return isTemplateFavorite ? item : null;
        } else if (item.type === "folder") {
          const filteredFolder = filterFolderForFavorites(item.data, layoutStore);
          return filteredFolder ? { ...item, data: filteredFolder } : null;
        } else if (item.type === "node") {
          const filteredNode = filterNavTreeForFavorites(item.data, layoutStore);
          return filteredNode ? { ...item, data: filteredNode } : null;
        }
        return null;
      })
      .filter((item): item is FavoriteItem => item !== null);
  }

  if (isFavorite || filteredItems.length > 0) {
    return {
      ...module,
      filteredItems,
      isFavorite,
    };
  }

  return null;
}

function filterFolderForFavorites(
  folder: ModuleFolderStructureItem,
  layoutStore: ReturnType<typeof useLayoutStore>,
): ModuleFolderStructureItem | null {
  // Filter templates: only keep favorite templates
  const filteredTemplates = folder.templates?.filter((template) => layoutStore.isFavorite(template.id ?? "")) ?? [];

  // Filter subfolders recursively: only keep if they have favorite templates inside
  const filteredFolders =
    folder.folders?.map((sub) => filterFolderForFavorites(sub, layoutStore)).filter((sub) => sub !== null) ?? [];

  const isFolderFavorite = layoutStore.isFavorite(folder.id);

  // Only return folder if it is favorite itself OR has favorite templates OR subfolders with favorites
  if (isFolderFavorite || filteredTemplates.length > 0 || filteredFolders.length > 0) {
    return {
      ...folder,
      templates: filteredTemplates.length > 0 ? filteredTemplates : undefined,
      folders: filteredFolders.length > 0 ? filteredFolders : undefined,
    };
  }

  return null;
}

const filteredModulesForFavorites = computed(() =>
  filteredModules.value
    .map((module) => filterModuleForFavorites(module, layoutStore, getModuleKey, getModuleTopLevelItems.value))
    .filter((m) => m !== null),
);

// Recursively filter nav tree nodes (settings, account) for favorites
function filterNavTreeForFavorites(
  node: NavNode | null,
  layoutStore: ReturnType<typeof useLayoutStore>,
): NavNode | null {
  if (!node) return null;
  const isFavorite = layoutStore.isFavorite(node.layoutStoreKey ?? "");

  if (isFavorite) {
    return { ...node, favorite: true };
  }

  const filteredChildren: NavNode[] = node.children
    ? node.children
        .map((child) => filterNavTreeForFavorites(child, layoutStore))
        .filter((child): child is NavNode => child !== null)
    : [];

  if (filteredChildren.length > 0) {
    return {
      ...node,
      children: filteredChildren,
      favorite: false,
    };
  }

  return null;
}

const filteredHomeNav = computed(() => filterNavTreeForFavorites(homeNav.value, layoutStore));
const filteredAccountingNav = computed(() => filterNavTreeForFavorites(accountingNav.value, layoutStore));
const filteredSettingsNav = computed(() => filterNavTreeForFavorites(settingsNav.value, layoutStore));
const filteredUserSettingsNav = computed(() => filterNavTreeForFavorites(userSettingsNav.value, layoutStore));
const filteredTenantManagementNav = computed(() => filterNavTreeForFavorites(tenantManagementNav.value, layoutStore));

const folderResults = useQueries({ queries: folderQueries });

const foldersStructure = computed(() => {
  return folderResults.value
    .filter((result) => result.data && !result.isError)
    .map((result) => {
      if (!result.data) {
        return null;
      }

      const moduleId = result.data.templates?.[0]?.moduleId ?? result.data.folders?.[0]?.moduleId;

      if (!moduleId) {
        return null;
      }

      return {
        moduleId,
        data: result.data,
      };
    })
    .filter((item) => item != null);
});

const refreshFolders = () => {
  folderResults.value.forEach((result) => {
    void result.refetch();
  });
};

provideRefreshTrigger(refreshFolders);

const mapFolderToNavNode = (folder: ModuleFolderStructureItem, moduleId: string): NavNode => {
  const children: NavNode[] = [];

  if (folder.folders?.length) {
    children.push(...folder.folders.map((sub) => mapFolderToNavNode(sub, moduleId)));
  }

  if (folder.templates?.length) {
    for (const template of folder.templates) {
      const permissions = workspace.getTemplatePermissions(template.id);
      if (!permissions.read || !template.showInNavigation) continue;

      children.push({
        label: template.name,
        layoutStoreKey: template.id,
        route: {
          name: Routes.MODULE,
          params: { moduleId: moduleId },
          query: { templateId: template.id },
        },
        indexId: template.indexId,
      });
    }
  }

  return {
    label: folder.label,
    layoutStoreKey: folder.id,
    children,
    indexId: folder.indexId,
    route: {
      name: Routes.MODULE,
      params: { moduleId },
      query: { folderId: folder.id },
    },
  };
};

const getModuleTopLevelItems = computed<(moduleId: string) => FavoriteItem[]>(() => (moduleId: string) => {
  const pinnedItems: FavoriteItem[] = getModulePinnedItems(moduleId);

  const obj = foldersStructure.value.find((o) => o.moduleId === moduleId);
  if (!obj) return pinnedItems;

  let templates = obj.data.templates ?? [];
  templates = templates.filter((t) => workspace.getTemplatePermissions(t.id).read && t.showInNavigation);
  const templateItems: FavoriteItem[] = templates.map((t) => ({ type: "template", data: t }));

  const folderItems: FavoriteItem[] = (obj.data.folders ?? []).map((f) => ({ type: "folder", data: f }));

  const allItems: FavoriteItem[] = [...pinnedItems, ...templateItems, ...folderItems];
  allItems.sort((a, b) => (a.data.indexId ?? Infinity) - (b.data.indexId ?? Infinity));
  return allItems;
});

const allModuleNodes = computed<NavNode[]>(() =>
  filteredModules.value.map((module) => ({
    label: maybeTranslateCommonTerm(module.name.default),
    icon: getModuleIcon(module.builtinModule),
    layoutStoreKey: getModuleKey(module.id),
    route: { name: Routes.MODULE, params: { moduleId: module.id }, query: { tenant: workspace.tenantId } },
    children: getModuleTopLevelItems
      .value(module.id)
      .map((item) => {
        if (item.type === "template") {
          return {
            label: "name" in item.data ? item.data.name : "",
            layoutStoreKey: item.data.id,
            route: {
              name: Routes.MODULE,
              params: { moduleId: module.id },
              query: { templateId: item.data.id, tenant: workspace.tenantId },
            },
            indexId: item.data.indexId,
          };
        } else if (item.type === "node") {
          return item.data;
        } else {
          return mapFolderToNavNode(item.data, module.id);
        }
      })
      .sort((a, b) => (a.indexId ?? Infinity) - (b.indexId ?? Infinity)),
  })),
);

const favoriteModuleNodes = computed<NavNode[]>(() =>
  filteredModulesForFavorites.value.map((module) => ({
    label: maybeTranslateCommonTerm(module.name.default),
    icon: getModuleIcon(module.builtinModule),
    layoutStoreKey: getModuleKey(module.id),
    route: { name: Routes.MODULE, params: { moduleId: module.id }, query: { tenant: workspace.tenantId } },
    children: (module.filteredItems ?? [])
      .map((item) => {
        if (item.type === "template") {
          return {
            label: "name" in item.data ? item.data.name : "",
            layoutStoreKey: item.data.id,
            route: {
              name: Routes.MODULE,
              params: { moduleId: module.id },
              query: { templateId: item.data.id, tenant: workspace.tenantId },
            },
            indexId: item.data.indexId,
          };
        } else if (item.type === "node") {
          return item.data;
        } else {
          return mapFolderToNavNode(item.data, module.id);
        }
      })
      .sort((a, b) => (a.indexId ?? Infinity) - (b.indexId ?? Infinity)),
  })),
);

const allNodes = computed<NavNode[]>(() => {
  return [
    homeNav.value,
    ...((workspace.tenantId || workspace.orgId) && workspace.tenant?.type === TenantType.MANDATOR_MANAGEMENT
      ? [tenantManagementNav.value]
      : []),
    ...allModuleNodes.value,
    ...((workspace.tenantId || workspace.orgId) && workspace.tenant?.accountingEnabled ? [accountingNav.value] : []),
    ...(workspace.tenantId || workspace.orgId ? [settingsNav.value] : []),
    userSettingsNav.value,
  ].filter((node): node is NavNode => node !== null && node !== undefined);
});

const favoriteNodes = computed<NavNode[]>(() => {
  return [
    filteredHomeNav.value,
    ...((workspace.tenantId || workspace.orgId) && workspace.tenant?.type === TenantType.MANDATOR_MANAGEMENT
      ? [filteredTenantManagementNav.value]
      : []),
    ...favoriteModuleNodes.value,
    ...(workspace.tenantId && workspace.orgId && workspace.tenant?.accountingEnabled
      ? [filteredAccountingNav.value]
      : []),
    ...(workspace.tenantId || workspace.orgId ? [filteredSettingsNav.value] : []),
    filteredUserSettingsNav.value,
  ].filter((node): node is NavNode => node !== null && node !== undefined);
});

const currentNodes = computed<NavNode[]>(() => {
  const isFavorites = layoutStore.sidebarNavContent === "favorites";
  return isFavorites ? favoriteNodes.value : allNodes.value;
});

const allNodeIds = computed<string[]>(() => {
  const ids: string[] = [];

  function collectIds(nodes: NavNode[], parentPath = "") {
    nodes.forEach((node) => {
      if (node.layoutStoreKey) {
        const currentPath = parentPath ? `${parentPath}.${node.layoutStoreKey}` : node.layoutStoreKey;
        ids.push(currentPath);

        if (node.children) {
          collectIds(node.children, currentPath);
        }
      } else if (node.children) {
        collectIds(node.children, parentPath);
      }
    });
  }

  collectIds(allNodes.value);

  return ids;
});

const layoutStoreItemConfigs = computed(() => layoutStore.navItemConfigs);

const isAllExpanded = computed(() =>
  allNodeIds.value.every((id) => layoutStoreItemConfigs.value[id]?.expanded !== false),
);
const isAllCollapsed = computed(() =>
  allNodeIds.value.every((id) => layoutStoreItemConfigs.value[id]?.expanded === false),
);

function setAllExpanded(expanded: boolean) {
  layoutStore.batchSetExpanded(allNodeIds.value, expanded);
}

onBeforeRouteUpdate(() => {
  if (isDesktop.value) return;
  if (isMini.value) return;
  if (!layoutStore.bars.navigation.visible) return;

  layoutStore.setBarVisibility("navigation", false);
});

watch(isMini, () => {
  searchTerm.value = "";
});

watch(
  allNodes,
  (nodes) => {
    layoutStore.setNavigationNodes(nodes);
  },
  { immediate: true, deep: true },
);

watch(isDesktop, (isNowDesktop, wasDesktop) => {
  if (wasDesktop === undefined) return;
  if (isNowDesktop === wasDesktop) return;
  if (isMini.value) return;

  if (!isNowDesktop) {
    if (layoutStore.bars.navigation.visible) {
      prevNavWasVisible.value = true;
      layoutStore.setBarVisibility("navigation", false);
    } else {
      prevNavWasVisible.value = false;
    }
  } else {
    if (prevNavWasVisible.value) {
      layoutStore.setBarVisibility("navigation", true);
    }
    prevNavWasVisible.value = null;
  }
});
</script>

<style lang="scss" module>
.panel {
  display: grid;
  grid-template-rows: min-content min-content auto;
  width: 100%;
  height: calc(100% - var(--sca-main-header-height));
  overflow: hidden;
  background-color: var(--color-background-default);
  border-top-left-radius: var(--border-radius-large);
  border-top-right-radius: var(--border-radius-large);

  @screen 2xl {
    grid-template-rows: min-content auto;
    height: var(--sca-content-max-height);
  }

  &.mini {
    width: 36px;
    height: 100%;
    margin-left: var(--space-component-padding-small);

    .sidebarNavTabs {
      padding: var(--dimension-100) var(--dimension-50);
      gap: var(--space-component-gap-small);
      padding-bottom: 0;
      border-bottom: 0;
      height: auto;
    }

    .sidebarNavInner {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-component-gap-medium);
    }

    .mainMenuSection {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0;
    }
  }

  .tenantOrgaSelect {
    padding: 10px var(--space-component-padding-xSmall);
    background-color: var(--color-background-neutral-onSubtle);
  }

  .innerPanel {
    overflow: auto;
    padding: var(--space-component-padding-small) 0;
  }

  .mainMenuSection {
    padding: 0 var(--space-component-padding-medium);
  }
}

.sidebarNavBackground {
  position: fixed;
  overflow: hidden;
  z-index: -2;
  background-color: rgba(0, 0, 0, 0.5);
  top: var(--sca-main-header-height);
  bottom: 0;
  left: 0;
  right: 0;
}

.sidebarNavTabs {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: var(--dimension-550);
  gap: var(--space-component-gap-xLarge);
  width: 100%;
  padding: var(--dimension-100) var(--dimension-150);
  border-bottom: solid 1px var(--color-border-neutral-subtle);
}

.enterFrom {
  opacity: 0;
  transform: translateX(12px);
}
.enterTo {
  opacity: 1;
  transform: translateX(0);
}
.leaveFrom {
  opacity: 1;
  transform: translateY(0);
}
.leaveTo {
  opacity: 0;
  transform: translateX(12px);
}
.enterActive,
.leaveActive {
  transition:
    opacity 0.1s ease,
    transform 0.1s ease;
}
</style>
